interface PaymentData {
  vatIncludedCountries: string[]
  supportedCurrencies: Currency[]
  pricePlans: PricePlan[]
  planHierarchy: PricePlanName[]
  paymentMethod: CardDetails | null
}

export const usePaymentStore = defineStore('payment', {
  state: (): PaymentData => ({
    vatIncludedCountries: ['CH', 'LI'],
    supportedCurrencies: ['EUR', 'CHF'],
    pricePlans: [],
    // The price plans we offer, ordered from cheapest to most expensive
    planHierarchy: ['Trial', 'Individual', 'Team', 'Pro', 'Scale'],
    paymentMethod: null,
  }),
  getters: {
    getPricePlanById: state => (planId: PricePlanId) => {
      return state.pricePlans.find(plan => plan.id === planId)
    },
    getSortedCurrencies: state => {
      const systemStore = useSystemStore()
      const { platform } = storeToRefs(systemStore)
      return platform.value === 'CH' ? state.supportedCurrencies.toReversed() : state.supportedCurrencies
    },
    getBillingFromPaymentMethod: state => {
      return state.paymentMethod?.billing
    },
  },
  actions: {
    async createPaymentIntent(
      paymentIntentPayload: PaymentIntentPayload | DirectPaymentIntentPayload,
      signal?: AbortSignal
    ) {
      const businessStore = useBusinessStore()
      const { paymentRepository } = useApi()
      if (businessStore.id) {
        return await paymentRepository.createPaymentIntent(businessStore.id, paymentIntentPayload, signal)
      } else {
        return await paymentRepository.createDirectPaymentIntent(paymentIntentPayload, signal)
      }
    },
    async directUpgradeSubscription(
      subscriptionPayload: UpgradeSubscriptionPayload,
      addressData: Address,
      currency: Currency,
      name: string
    ) {
      const userStore = useUserStore()
      const { paymentRepository } = useApi()
      await paymentRepository.directUpgradeSubscription({
        ...subscriptionPayload,
        ...addressData,
        currency: currency,
        name: name,
        gtcAccepted: true,
      })

      await userStore.fetchUser()
      await userStore.loadUserBusinessData()
    },
    async upgradeSubscription(subscriptionPayload: UpgradeSubscriptionPayload) {
      const businessStore = useBusinessStore()
      const userStore = useUserStore()
      const { paymentRepository } = useApi()

      const updatedPricePlan = await paymentRepository.upgradeSubscription(businessStore.id, subscriptionPayload)

      businessStore.$patch({ pricePlan: updatedPricePlan })
      await userStore.loadUserBusinessData()
    },
    async upgradeTrial(type: string) {
      const businessStore = useBusinessStore()
      const { paymentRepository } = useApi()
      await paymentRepository.upgradeTrial(businessStore.id, type)
    },
    async getBusinessPricePlans(): Promise<PricePlan[]> {
      const { paymentRepository } = useApi()

      const pricePlans = await paymentRepository.getPricePlans()

      const namedPricePlans: PricePlan[] = pricePlans
        .filter(plan => !plan.id.startsWith('TRIAL'))
        .map(plan => {
          const inferredName = plan.id.split('_').shift()

          return {
            ...plan,
            name: (inferredName ? inferredName.charAt(0) + inferredName.slice(1).toLowerCase() : '') as PricePlanName,
          }
        })

      this.$patch({ pricePlans: namedPricePlans })

      return namedPricePlans
    },
    async getCurrentPaymentMethod(): Promise<CardDetails | null> {
      const businessStore = useBusinessStore()
      const { paymentRepository } = useApi()
      const paymentMethod = await paymentRepository.getCurrentPaymentMethod(businessStore.id)
      this.$patch({ paymentMethod })
      return paymentMethod
    },
    async getActiveSubscription(businessId: string) {
      const { paymentRepository } = useApi()
      return await paymentRepository.getActiveSubscription(businessId)
    },
    async getCreditsBalance(businessId: string) {
      const { paymentRepository } = useApi()
      return await paymentRepository.getCreditsBalance(businessId)
    },
  },
})
